import React, { useState } from "react";

import {
  Table,
  Form,
  Input,
  message as antdMessage,
  Alert,
  Skeleton,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import { Plus } from "lucide-react";

import type { ColumnsType } from "antd/es/table";

import FormModal from "../../common/modal/FormModal";

import { PaymentMethod } from "../../../bitmaker/schemas/Billing/Payment";

import { usePaymentMethods } from "../../../hooks/usePaymentMethods";
import { useApiError } from "../../../hooks/useApiError";

import { HTTPMethod, methodMap } from "../../../services/BitmakerApp";

import { MyFormItem } from "../MyFormItem";

export default function PaymentMethodsManagement() {
  const {
    paymentMethods,
    loadingPaymentMethods,
    fetchPaymentMethodsError,
    refetchPaymentMethods,
  } = usePaymentMethods();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();

  const [editingMethod, setEditingMethod] = useState<PaymentMethod | null>(
    null,
  );

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { handleAPIError } = useApiError();

  const handleEditModal = (record: PaymentMethod) => {
    setEditingMethod(record);
    form.setFieldsValue({ name: record.name.trim() });
    setIsModalOpen(true);
    setSubmitError(null);
  };

  const handleAddModal = () => {
    setEditingMethod(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const handleCancelModal = () => {
    setSubmitError(null);
    setIsModalOpen(false);
  };

  const getPaymentMethodErrorMessage = (
    status: number | null,
    isEditing: boolean,
  ) => {
    if (status === 409) {
      return "A payment method with this name already exists.";
    }

    return `Error ${isEditing ? "updating" : "creating"
      } payment method. Please try again.`;
  };

  const handleSubmit = (values: { name: string }) => {
    if (isSubmitting) return;

    setSubmitError(null);
    setIsSubmitting(true);

    const isEditing = editingMethod !== null;

    const endpoint = isEditing
      ? {
        method: "put",
        url: `/billing/settings/payment-methods/${editingMethod?.id}`,
      }
      : { method: "post", url: "/billing/settings/payment-methods" };

    const method = endpoint.method as HTTPMethod;

    const dataToSend = {
      name: values.name.trim(),
    };

    methodMap[method](endpoint.url, dataToSend, (response) => {
      const { isError, status } = handleAPIError(response);

      if (isError) {
        const errorMessage = getPaymentMethodErrorMessage(status, isEditing);
        setSubmitError(errorMessage);
        setIsSubmitting(false);
        return;
      }

      antdMessage.success(
        `Payment method "${response.data.name}" ${isEditing ? "updated" : "created"
        } successfully!`,
      );

      setIsModalOpen(false);
      setEditingMethod(null);
      refetchPaymentMethods();
      setIsSubmitting(false);
    });
  };

  const columns: ColumnsType<PaymentMethod> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "85%",
      render: (name: string) => (
        <span className="font-medium text-gray-700">{name}</span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: "15%",
      render: (_, record) => (
        <button
          type="button"
          onClick={() => handleEditModal(record)}
          className="btn btn-small btn-indigo"
          disabled={loadingPaymentMethods}
        >
          <EditOutlined size={14} />
          <span>Edit</span>
        </button>
      ),
    },
  ];

  const isInitialLoading = loadingPaymentMethods && paymentMethods.length === 0;
  const fetchError = !!fetchPaymentMethodsError;

  return (
    <div>
      {/* Header + primary action */}
      <div className="flex justify-between items-start gap-4 mb-6">
        <div className="flex flex-col gap-1 min-w-0">
          <h2 className="text-2xl text-gray-800">Payment Methods</h2>
          <p className="text-gray-600 text-sm">
            Define how clients can pay you (e.g. bank transfer, credit card).
            These appear when recording invoice payments.
          </p>
        </div>
        <button
          type="button"
          onClick={handleAddModal}
          className="btn btn-green-2 shrink-0"
          disabled={loadingPaymentMethods}
        >
          <Plus size={16} />
          <span>Add Payment Method</span>
        </button>
      </div>

      {/* Error */}
      {fetchError && (
        <div className="mb-4">
          <Alert
            type="error"
            showIcon
            message="Failed to load payment methods"
            description="Something went wrong while fetching data. Please try again."
            action={
              <button
                type="button"
                className="btn btn-gray"
                onClick={refetchPaymentMethods}
                disabled={loadingPaymentMethods}
              >
                Retry
              </button>
            }
          />
        </div>
      )}

      {/* Table / Loading */}
      {isInitialLoading ? (
        <div className="mt-4 space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="py-2">
              <Skeleton
                active
                title={false}
                paragraph={{ rows: 1, width: "100%" }}
              />
            </div>
          ))}
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={paymentMethods}
          rowKey="id"
          loading={loadingPaymentMethods}
          pagination={false}
          rowClassName={() => "hover:bg-gray-50 transition-colors"}
          locale={{
            emptyText: fetchError ? (
              "Unable to load payment methods"
            ) : (
              <div className="py-10 flex flex-col items-center justify-center gap-3 text-center">
                <div className="text-gray-500 text-sm">
                  You don't have any payment methods yet
                </div>
                <button
                  type="button"
                  className="btn btn-gray"
                  onClick={handleAddModal}
                  disabled={loadingPaymentMethods}
                >
                  <Plus size={16} />
                  <span>Add Payment Method</span>
                </button>
              </div>
            ),
          }}
          bordered
        />
      )}

      {/* Add/Edit Payment Method Form Modal */}
      <FormModal
        title={
          editingMethod
            ? `Edit Payment Method: ${editingMethod.name}`
            : "Add New Payment Method"
        }
        open={isModalOpen}
        onCancel={handleCancelModal}
        form={form}
        formId="addPaymentMethodForm"
        onFinish={handleSubmit}
        isSubmitting={isSubmitting}
        submitLabel={editingMethod ? "Update" : "Save"}
        error={submitError}
        afterClose={() => {
          form.resetFields();
          setEditingMethod(null);
          setSubmitError(null);
        }}
      >
        <MyFormItem
          label="Method name"
          name="name"
          rules={[
            {
              required: true,
              message: "Please enter the payment method name.",
            },
          ]}
        >
          <Input
            placeholder="e.g. Bank transfer, Wise, Visa"
            onChange={() => submitError && setSubmitError(null)}
            onBlur={(e) => {
              const value = e.target.value.trim();
              if (value !== e.target.value) {
                form.setFieldsValue({ name: value });
              }
            }}
          />
        </MyFormItem>
      </FormModal>
    </div>
  );
}
